/**
 * Restricted first-try pick + save. CTA uses search / last-ordered /
 * highest spend — never table row 1. Sheet save must not flip Show Supplier
 * or catalogue.
 */

import { isInternalOrFixtureTitle } from './growthEngineInternalTitle'
import { findSuggestedVenueSupplier } from './growthEngineRecommendation'

const asTrimmedString = value => {
  if (value == null) return ''
  return String(value).trim()
}

const asMillis = value => {
  if (value == null || value === '') return 0
  const n = Date.parse(value)
  return Number.isFinite(n) ? n : 0
}

const suggestedSpend = item =>
  Number(item?.orderCount) ||
  Number(item?.spend) ||
  Number(item?.totalSpend) ||
  0

export function venueSupplierDisplayTitle(supplier) {
  return asTrimmedString(
    supplier?.supplierTitle || supplier?.title || supplier?.supplier?.title,
  )
}

export function isFixtureVenueSupplier(supplier) {
  return isInternalOrFixtureTitle(venueSupplierDisplayTitle(supplier))
}

export function isRestrictableVenueSupplier(supplier) {
  if (!supplier || !supplier._id) return false
  if (supplier.restricted === true) return false
  if (isFixtureVenueSupplier(supplier)) return false
  return true
}

export function listRestrictableVenueSuppliers(venueSuppliers) {
  return (Array.isArray(venueSuppliers) ? venueSuppliers : []).filter(
    isRestrictableVenueSupplier,
  )
}

function looksPlaceholderSupplierTitle(title) {
  const value = String(title || '').trim()
  if (!value) return true
  return /test/i.test(value) || /^\d{1,3}\b/.test(value)
}

function rankRestrictCandidates(candidates) {
  return [...candidates].sort((a, b) => {
    if (b.lastOrderedAt !== a.lastOrderedAt) {
      return b.lastOrderedAt - a.lastOrderedAt
    }
    return b.spend - a.spend
  })
}

/**
 * Chip / search / last-ordered / highest spend. Never table row 1 when
 * there is a better signal. Never a fixture. Never invents an id.
 */
export function pickExistingSupplierForFirstRestrict({
  item,
  searchQuery,
  venueSuppliers,
  suggestedItems = [],
} = {}) {
  const restrictable = listRestrictableVenueSuppliers(venueSuppliers)
  if (!restrictable.length) return null

  if (item && typeof item === 'object') {
    const matched = findSuggestedVenueSupplier(restrictable, item)
    if (matched && !isFixtureVenueSupplier(matched)) return matched
  }

  const query = asTrimmedString(searchQuery).toLowerCase()
  if (query) {
    const exact = restrictable.find(
      supplier => venueSupplierDisplayTitle(supplier).toLowerCase() === query,
    )
    if (exact) return exact
    const partial = restrictable.find(supplier =>
      venueSupplierDisplayTitle(supplier).toLowerCase().includes(query),
    )
    if (partial) return partial
  }

  const fromSuggested = (Array.isArray(suggestedItems) ? suggestedItems : [])
    .map(hint => {
      const supplier = findSuggestedVenueSupplier(restrictable, hint)
      if (!supplier) return null
      return {
        supplier,
        lastOrderedAt: asMillis(hint?.lastOrderedAt || supplier.lastOrderedAt),
        spend: suggestedSpend(hint),
      }
    })
    .filter(Boolean)
  const bestSuggested = rankRestrictCandidates(fromSuggested)[0]
  if (bestSuggested) return bestSuggested.supplier

  const fromRows = restrictable.map(supplier => ({
    supplier,
    lastOrderedAt: asMillis(supplier.lastOrderedAt || supplier.lastOrderDate),
    spend: Number(supplier.spend || supplier.totalSpend || 0),
  }))
  const bestRow = rankRestrictCandidates(fromRows)[0]
  if (bestRow && (bestRow.lastOrderedAt > 0 || bestRow.spend > 0)) {
    return bestRow.supplier
  }

  const preferred = restrictable.find(
    supplier =>
      !looksPlaceholderSupplierTitle(venueSupplierDisplayTitle(supplier)),
  )
  if (preferred) return preferred
  return null
}

/**
 * First-try save: restrict + who approves only. Keep Show Supplier / catalogue.
 */
export function buildFirstTryRestrictSavePayload(
  venueSupplier,
  { restricted, approvalEmails } = {},
) {
  if (!venueSupplier || typeof venueSupplier !== 'object') return null
  return {
    ...venueSupplier,
    restricted: restricted === true,
    approvalEmails: Array.isArray(approvalEmails) ? approvalEmails : [],
    isActive: venueSupplier.isActive,
    isCatalogueAccessible: venueSupplier.isCatalogueAccessible,
  }
}

export function defaultFirstTryApprovalEmails(venueSupplier, venue) {
  const existing = Array.isArray(venueSupplier?.approvalEmails)
    ? venueSupplier.approvalEmails.filter(Boolean)
    : []
  if (existing.length) return existing
  const owner = asTrimmedString(venue?.account?.emailAddress)
  return owner ? [owner] : []
}
